import { PsHttpService, type PatSnapSuccessResponseData } from '@patsnap/tahiti-core/common/ps-http';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import { accountServiceApiUrlPrefix } from '@eureka/deprecated/config/constants';
import { getAsyncToken } from '@eureka/startup/src/enable-token-base';

import type { ApiGetTaskIdRequest, GetTaskId } from '@/infrastructure/api-functions/interface/get-task-id.interface';
import type { PutHistory, ApiPutHistoryRequest } from '@/infrastructure/api-functions/interface/put-history.interface';
import type {
    GetHistoryDetail,
    ApiGetHistoryDetailResponse,
} from '@/infrastructure/api-functions/interface/get-history-detail.interface';
import type { SSEStreamService } from '@patsnap-utils/sse';
import { SSEStreamServiceImpl } from '@patsnap-utils/sse';
import psHttp from '@patsnap/common-ps-http';

import type {
    ApiCheckSensitiveWordRequest,
    ApiCheckSensitiveWordResponse,
    CheckSensitiveWord,
} from './interface/check-sensitive-word.interface';

import type {
    GetTechMeans,
    ApiGetTechMeansRequest,
    ApiGetTechMeansResponse,
} from '@/infrastructure/api-functions/interface/get-tech-means.interface';
import type {
    PutTechMeans,
    ApiPutTechMeansRequest,
    ApiPutTechMeansResponse,
} from '@/infrastructure/api-functions/interface/put-tech-means.interface';
import type {
    GetDisclosureTitle,
    ApiGetDisclosureTitleRequest,
} from '@/infrastructure/api-functions/interface/get-disclosure-title.interface';
import type {
    GetDisclosureContent,
    ApiGetDisclosureContentRequest,
    ApiGetDisclosureContentResponse,
} from '@/infrastructure/api-functions/interface/get-disclosure-content.interface';
import type {
    PutDisclosureSetting,
    ApiPutDisclosureSettingRequest,
} from '@/infrastructure/api-functions/interface/put-disclosure-setting.interface';
import type {
    InitDisclosureSetting,
    ApiInitDisclosureSettingRequest,
} from '@/infrastructure/api-functions/interface/init-disclosure-setting.interface';
import type {
    ExportDisclosure,
    ApiExportDisclosureRequest,
} from '@/infrastructure/api-functions/interface/export-disclosure.interface';
import type {
    GetContentStorage,
    ApiGetContentStorageRequest,
    ApiGetContentStorageResponse,
} from '@/infrastructure/api-functions/interface/get-content-storage.interface';

import { getCurrentLanguage } from '@eureka/language-tool'

export const CoreSearchApiService = Symbol('CoreSearchApiService');

export type CoreSearchApiService = PsHttpService;

const baseUrl = `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka`;
@injectable()
export class CoreSearchApiServiceImpl extends PsHttpService
    implements CheckSensitiveWord,
        GetTaskId,
        PutHistory,
        GetHistoryDetail,
        GetTechMeans,
        PutTechMeans,
        GetDisclosureTitle,
        GetDisclosureContent,
        PutDisclosureSetting,
        InitDisclosureSetting,
        ExportDisclosure,
        GetContentStorage {
    get options() {
        return {
            baseURL: baseUrl,
            headers: {
                'X-API-Version': '1.0',
            },
        };
    }

    /*
    * 该请求用于SSE接口刷新token使用，直接使用psHttp调用SSE接口，仍会发生长连接的问题
    */
    private _refreshToken() {
        return psHttp({
            url: `${accountServiceApiUrlPrefix}/account-info/extra-data`,
            method: 'get',
            headers: {
                'Content-Type': 'application/json',
            },
        });
    }

    async checkSensitiveWord(data: ApiCheckSensitiveWordRequest) {
        const res = await this.post<PatSnapSuccessResponseData<ApiCheckSensitiveWordResponse>>({
            url: '/patent-disclosure/sensitive-word',
            data,
            cache: true,
            transformCode: true,
        });
        return res.data;
    }

    // 历史相关接口
    async getTaskId(params: ApiGetTaskIdRequest) {
        const res = await this.get<PatSnapSuccessResponseData<string>>({
            url: '/patent-disclosure/task-id',
            params,
            transformCode: true,
        });
        return res.data;
    }

    async putHistory(data: ApiPutHistoryRequest) {
        const res = await this.put<PatSnapSuccessResponseData<void>>({
            url: '/patent-disclosure/task-info',
            data,
            transformCode: true,
        });
        return res.data;
    }

    async getHistoryDetail(taskId: string) {
        const res = await this.get<PatSnapSuccessResponseData<ApiGetHistoryDetailResponse>>({
            url: `patent-disclosure/task-info/${taskId}`,
            transformCode: true,
        });
        return res.data;
    }

    async getTechMeans(data: ApiGetTechMeansRequest) {
        const res = await this.post<PatSnapSuccessResponseData<ApiGetTechMeansResponse>>({
            url: '/patent-disclosure/tech-means',
            data,
            transformCode: true,
        });
        return res.data;
    }

    async putTechMeans(data: ApiPutTechMeansRequest) {
        const res = await this.put<PatSnapSuccessResponseData<ApiPutTechMeansResponse>>({
            url: '/patent-disclosure/tech-means',
            data,
            transformCode: true,
        });
        return res.data;
    }

    async getDisclosureTitle(data: ApiGetDisclosureTitleRequest) {
        const res = await this.post<PatSnapSuccessResponseData<string>>({
            url: '/patent-disclosure/title',
            data,
            transformCode: true,
        });
        return res.data;
    }

    getDisclosureContent(data: ApiGetDisclosureContentRequest<any>): SSEStreamService<ApiGetDisclosureContentResponse> {
        return new SSEStreamServiceImpl({
            url: `${baseUrl}/patent-disclosure/content`,
            data,
            headerOptions: {
                asyncToken: getAsyncToken,
                lang: getCurrentLanguage(),
                from: 'w-eureka',
            },
            refreshToken: this._refreshToken as unknown as () => Promise<void>,
        });
    }

    async putDisclosureSetting(data: ApiPutDisclosureSettingRequest) {
        const res = await this.put<PatSnapSuccessResponseData<void>>({
            url: '/patent-disclosure/setting',
            data,
            transformCode: true,
        });
        return res.data;
    }

    async initDisclosureSetting(data: ApiInitDisclosureSettingRequest) {
        const res = await this.put<PatSnapSuccessResponseData<void>>({
            url: '/patent-disclosure/setting/init',
            data,
            transformCode: true,
        });
        return res.data;
    }

    async exportDisclosure(data: ApiExportDisclosureRequest) {
        const res = await this.post<PatSnapSuccessResponseData<string>>({
            url: '/patent-disclosure/export',
            data,
            transformCode: true,
        });
        return res.data;
    }

    async getContentStorage({ cache_id }: ApiGetContentStorageRequest) {
        const res = await this.get<PatSnapSuccessResponseData<ApiGetContentStorageResponse>>({
            url: '/disclosure/tech/paste',
            params: { cache_id },
            transformCode: true,
        });
        return res.data;
    }
}
